/**
 * Resolves the display name of the user who created / last modified a record.
 *
 * AR_ScheduledBilling, AR_ScheduledBillingItem, AR_Invoice and AR_InvoiceItem
 * carry the denormalised `ixCreatedByName` / `ixModByName` columns, so the name
 * can be read straight off the record. When those columns are empty (records
 * created before the columns existed, or rows the source system did not stamp)
 * we fall back to the previous behaviour of resolving the `createdBy` / `modBy`
 * user ID against the user listing.
 */

interface AuditUser {
  ID?: string
  name?: string
}

export const getUserNameByID = (
  userList: AuditUser[],
  userID?: string
): string | undefined =>
  userID ? userList?.find(user => user?.ID === userID)?.name : undefined

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const resolveName = (
  storedValue: any,
  userList: AuditUser[],
  userID?: string
): string | undefined => {
  const stored =
    typeof storedValue === 'string' ? storedValue.trim() : undefined

  if (!stored) return getUserNameByID(userList, userID)

  if (UUID_PATTERN.test(stored))
    return (
      getUserNameByID(userList, stored) ?? getUserNameByID(userList, userID)
    )

  return stored
}

export const getCreatedByName = (
  record: any,
  userList: AuditUser[]
): string | undefined =>
  resolveName(record?.ixCreatedByName, userList, record?.createdBy)

export const getModByName = (
  record: any,
  userList: AuditUser[]
): string | undefined =>
  resolveName(record?.ixModByName, userList, record?.modBy)
